import { Link } from "react-router-dom";
import {
  HeartPulse, TrafficCone, CircleParking, Route, GitFork, ShieldAlert, Sparkles, BadgeCheck,
  Users, Brain, Car, UserCheck, TrendingUp, ShieldCheck, BadgeIndianRupee, Heart, Gauge,
  IdCard, RefreshCw, Copy, PlusCircle, FileText, Eye, Focus, Moon, Navigation, Play, Clock,
  Building2, Handshake, GraduationCap, Megaphone, CheckCircle2, Compass, Target, Zap, Award,
  BookOpen, Phone, MessageCircle, ArrowRight, MapPin, Quote, CloudRain, History, Snowflake, Home,
  CalendarClock, Headphones, Infinity as InfinityIcon, Warehouse,
} from "lucide-react";
import { Reveal, SectionHeading, PrimaryButton, SecondaryButton, MaskedLines } from "./primitives";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { SITE, waLink } from "../data/site";
import { useLeadModal } from "./LeadModalContext";
import { useLanguage , tr } from "../lib/language";
import { track } from "../lib/track";

const ICONS = {
  HeartPulse, TrafficCone, CircleParking, Route, GitFork, ShieldAlert, Sparkles, BadgeCheck,
  Users, Brain, Car, UserCheck, TrendingUp, ShieldCheck, BadgeIndianRupee, Heart, Gauge,
  IdCard, RefreshCw, Copy, PlusCircle, FileText, Eye, Focus, Moon, Navigation, Play, Clock,
  Building2, Handshake, GraduationCap, Megaphone, CheckCircle2, Compass, Target, Zap, Award,
  BookOpen, Phone, MessageCircle, MapPin, Quote, CloudRain, History, Snowflake, Home,
  CalendarClock, Headphones, Infinity: InfinityIcon, Warehouse,
};

export const Icon = ({ name, className = "h-6 w-6" }) => {
  const Cmp = ICONS[name] || Car;
  return <Cmp className={className} aria-hidden="true" />;
};

export const PageHero = ({ eyebrow, lines, text, image, imageAlt, children }) => (
  <section className="relative overflow-hidden bg-dwc-soft pb-16 pt-32 md:pb-24 md:pt-40">
    <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-dwc-green/10" aria-hidden="true" />
    <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-dwc-green/10" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
      <div className={`grid items-center gap-12 ${image ? "lg:grid-cols-2" : ""}`}>
        <div>
          <Reveal>
            <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-dwc-green">
              <span className="h-1.5 w-1.5 rounded-full bg-dwc-crimson" aria-hidden="true" />
              {tr(eyebrow)}
            </p>
          </Reveal>
          <h1 className="mt-5 text-4xl font-extrabold tracking-tight text-dwc-charcoal sm:text-5xl lg:text-6xl leading-[1.05] text-balance">
            <MaskedLines lines={lines} delay={0.1} />
          </h1>
          {text && (
            <Reveal delay={0.35}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-dwc-charcoal/70 md:text-lg">{tr(text)}</p>
            </Reveal>
          )}
          {children && <Reveal delay={0.45}><div className="mt-8 flex flex-wrap gap-4">{children}</div></Reveal>}
        </div>
        {image && (
          <Reveal delay={0.2} y={40}>
            <div className="relative">
              <img src={image} alt={imageAlt || ""} loading="eager" className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-[0_30px_80px_rgba(23,107,58,0.18)]" />
              <div className="absolute -bottom-5 -left-5 h-full w-full rounded-[2rem] border-2 border-dwc-green/20" aria-hidden="true" />
            </div>
          </Reveal>
        )}
      </div>
    </div>
  </section>
);

export const BookButtons = ({ dark = false, bookTestId = "section-book-btn", waTestId = "section-whatsapp-btn" }) => {
  const { open } = useLeadModal();
  const { t } = useLanguage();
  return (
    <>
      <PrimaryButton onClick={() => open()} testId={bookTestId}>{t.book}</PrimaryButton>
      <a
        href={waLink()}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={waTestId}
        onClick={() => track("whatsapp_click", { source: "page_cta" })}
        className={`group inline-flex items-center justify-center gap-2 rounded-xl border-2 px-7 py-4 text-sm font-bold uppercase tracking-wide transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 ${
          dark ? "border-white/40 text-white hover:border-white hover:bg-white/10" : "border-dwc-green bg-white text-dwc-green hover:bg-dwc-soft"
        }`}
      >
        <MessageCircle className="h-4 w-4" aria-hidden="true" />
        {t.whatsapp}
      </a>
    </>
  );
};

export const CTASection = ({ title = "Stop Avoiding Driving. Start Building Your Driving Confidence.", sub = "Take the first step towards becoming confident enough to drive on your own.", chapter = "18" }) => {
  const { open } = useLeadModal();
  const { t } = useLanguage();
  return (
    <section className="relative overflow-hidden bg-dwc-charcoal py-24 md:py-32 grain" data-testid="final-cta-section">
      <div className="pointer-events-none absolute -left-32 top-0 h-[28rem] w-[28rem] rounded-full bg-dwc-green/25 blur-[120px]" aria-hidden="true" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-[22rem] w-[22rem] rounded-full bg-dwc-crimson/15 blur-[120px]" aria-hidden="true" />
      <div className="relative mx-auto max-w-5xl px-5 text-center lg:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-300">
            {chapter} — <span className="text-dwc-crimson">Your Move</span>
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl leading-[1.05] text-balance">
            {tr(title)}
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mx-auto mt-6 max-w-2xl text-base text-white/70 md:text-lg">{tr(sub)}</p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <PrimaryButton onClick={() => open()} testId="final-cta-book-btn">{t.book}</PrimaryButton>
            <SecondaryButton dark href={waLink()} testId="final-cta-whatsapp-btn" onClick={() => track("whatsapp_click", { source: "final_cta" })}>
              <MessageCircle className="h-4 w-4" aria-hidden="true" /> {t.whatsapp}
            </SecondaryButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const FAQSection = ({ faqs, chapter = "11", title = "Questions? We Have Honest Answers." }) => (
  <section className="bg-white py-24 md:py-32" data-testid="faq-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
        <div>
          <SectionHeading
            eyebrow="Frequently Asked"
            number={chapter}
            title={tr(title)}
            text="Have questions about our training, programs or guarantee? Chat with us on WhatsApp."
          />
          <Reveal delay={0.2}>
            <div className="mt-8">
              <BookButtons bookTestId="faq-book-btn" waTestId="faq-whatsapp-btn" />
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-gray-100">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-5 text-left text-[15px] font-bold text-dwc-charcoal hover:text-dwc-green hover:no-underline">
                  {tr(f.q)}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-dwc-charcoal/70">
                  {tr(f.a)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </div>
  </section>
);

export const StoryCard = ({ story, featured = false, index = 0 }) => (
  <Reveal delay={index * 0.08} className="h-full">
    <article
      data-testid={`story-card-${index}`}
      className={`group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(23,107,58,0.12)] ${featured ? "md:col-span-2 md:flex-row" : ""}`}
    >
      <div className={`relative overflow-hidden ${featured ? "md:w-2/5" : ""}`}>
        {story.image ? (
          <img
            src={story.image}
            alt={`${story.name} — DWC student from ${story.location}`}
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${featured ? "h-72 md:h-full" : "h-60"}`}
          />
        ) : (
          <div className={`flex w-full items-center justify-center bg-dwc-soft ${featured ? "h-72 md:h-full" : "h-60"}`} aria-hidden="true">
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-dwc-green text-3xl font-black text-white">
              {(story.name || "D").replace(/^(Mrs?\.|Ms\.|Mr\.)\s*/i, "").charAt(0)}
            </span>
          </div>
        )}
        <span className="absolute left-4 top-4 rounded-full bg-dwc-green px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
          {story.program}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6 md:p-7">
        <Quote className="h-6 w-6 text-dwc-green/30" aria-hidden="true" />
        <p className="mt-3 text-sm leading-relaxed text-dwc-charcoal/75">
          <span className="font-semibold text-dwc-charcoal">The challenge:</span> {story.challenge}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-dwc-charcoal/75">
          <span className="font-semibold text-dwc-green">The result:</span> {story.result}
        </p>
        <div className="mt-auto flex items-center gap-2 pt-5">
          <span className="h-px w-6 bg-dwc-crimson" aria-hidden="true" />
          <p className="text-sm font-extrabold text-dwc-charcoal">{story.name}</p>
          <span className="text-xs text-dwc-charcoal/50">· {story.location}</span>
        </div>
      </div>
    </article>
  </Reveal>
);

export const LevelCard = ({ level, index }) => (
  <Reveal delay={index * 0.12} className="h-full">
    <article
      data-testid={`level-card-${level.slug}`}
      className={`relative flex h-full flex-col rounded-3xl p-8 transition-[box-shadow,transform] duration-300 hover:-translate-y-1.5 ${
        level.highlighted
          ? "border-2 border-dwc-crimson/60 bg-white shadow-[0_24px_60px_rgba(181,18,43,0.10)]"
          : "border border-gray-100 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]"
      }`}
    >
      {level.highlighted && (
        <span className="absolute -top-3.5 left-8 rounded-full bg-dwc-crimson px-4 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-white">
          Most Transformative
        </span>
      )}
      <p className={`text-5xl font-black tracking-tight ${level.highlighted ? "text-dwc-crimson/15" : "text-dwc-green/15"}`}>{level.num}</p>
      {level.stage && (
        <p className="mt-2 text-[11px] font-extrabold uppercase tracking-[0.22em] text-dwc-crimson">{level.stage}</p>
      )}
      <h3 className="mt-2 text-2xl font-extrabold uppercase tracking-tight text-dwc-charcoal">{level.name}</h3>
      <p className="mt-1 text-sm font-semibold text-dwc-green">{level.promise}</p>
      <ul className="mt-6 flex-1 space-y-2.5">
        {level.items.map((item) => (
          <li key={item} className="flex items-start gap-2.5 text-sm text-dwc-charcoal/75">
            <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${level.highlighted ? "text-dwc-crimson" : "text-dwc-green"}`} aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
      <Link
        to={`/programs/${level.slug}`}
        data-testid={`level-cta-${level.slug}`}
        className={`group mt-8 inline-flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide transition-colors ${
          level.highlighted ? "text-dwc-crimson hover:text-dwc-crimson-dark" : "text-dwc-green hover:text-dwc-green-dark"
        }`}
      >
        {level.cta}
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
      </Link>
    </article>
  </Reveal>
);
